(() => {
  if (window.ScQuickOrderCart) return;
  const SOURCE = '_sc_quick_order_source';
  const GIFT = '_sc_quick_order_gift';
  const RULE = '_sc_quick_order_gift_rule';

  function variantId(item) {
    return String(item.variant_id || item.id || '');
  }

  function storedRule(cart) {
    try { return JSON.parse(cart.attributes?.[RULE] || 'null'); } catch (_) { return null; }
  }

  function sameProperties(left = {}, right = {}) {
    const keys = Object.keys(left || {});
    return keys.length === Object.keys(right || {}).length && keys.every((key) => left[key] === right[key]);
  }

  function sellingPlanId(item) {
    return String(item.selling_plan_allocation?.selling_plan?.id || '');
  }

  function allocatedGiftLine(cart) {
    const rule = storedRule(cart);
    if (Number(rule?.gift?.quantity) !== 1) return null;
    const candidates = (cart.items || []).filter((item) =>
      variantId(item) === String(rule.variantId) && item.quantity > 0
    );
    return candidates.find((item) => item.key === rule.gift.lineKey) || candidates.find((item) =>
      sameProperties(item.properties, rule.gift.properties) && sellingPlanId(item) === String(rule.gift.sellingPlanId || '')
    ) || null;
  }

  function giftUnits(cart) {
    const result = new Map((cart.items || []).filter((item) => item.properties?.[GIFT] === '1').map((item) => [item.key, Number(item.quantity)]));
    const allocated = allocatedGiftLine(cart);
    if (allocated) result.set(allocated.key, 1);
    return result;
  }

  function linesByVariant(cart) {
    const gifts = giftUnits(cart);
    const result = new Map();
    (cart.items || []).forEach((item) => {
      const quantity = Number(item.quantity) - (gifts.get(item.key) || 0);
      if (quantity <= 0) return;
      const id = variantId(item);
      if (!result.has(id)) result.set(id, []);
      result.get(id).push({ ...item, quantity });
    });
    return result;
  }

  function quantities(cart) {
    return new Map([...linesByVariant(cart)].map(([id, items]) => [
      id, items.reduce((total, item) => total + Number(item.quantity), 0),
    ]));
  }

  function preferredLine(lines) {
    return lines.find((item) => !item.selling_plan_allocation && item.properties?.[SOURCE] === 'schnellbestellliste') ||
      lines.find((item) => !item.selling_plan_allocation && Object.keys(item.properties || {}).length === 0) ||
      lines.find((item) => !item.selling_plan_allocation) || lines[0];
  }

  function buildUpdates(cart, targets) {
    const groups = linesByVariant(cart);
    const gifts = giftUnits(cart);
    const updates = {};
    targets.forEach(({ id, quantity }) => {
      const lines = groups.get(String(id)) || [];
      const current = lines.reduce((total, item) => total + Number(item.quantity), 0);
      if (quantity > current) {
        const existing = preferredLine(lines);
        if (existing) updates[existing.key] = existing.quantity + (gifts.get(existing.key) || 0) + quantity - current;
        else {
          // A line containing only the allocated gift can also receive purchased units.
          const allocated = allocatedGiftLine(cart) || (cart.items || []).find((item) => variantId(item) === String(id) && gifts.has(item.key));
          if (allocated && variantId(allocated) === String(id)) updates[allocated.key] = allocated.quantity + quantity;
          else updates[String(id)] = quantity;
        }
      } else {
        let remaining = current - quantity;
        const ordered = [...lines].sort((left, right) =>
          Number(right.properties?.[SOURCE] === 'schnellbestellliste') - Number(left.properties?.[SOURCE] === 'schnellbestellliste')
        );
        for (const item of ordered) {
          if (!remaining) break;
          const remove = Math.min(remaining, item.quantity);
          updates[item.key] = item.quantity - remove + (gifts.get(item.key) || 0);
          remaining -= remove;
        }
      }
    });
    return updates;
  }

  function eligibleSubtotal(cart, factor = 1, eligibleVariantIds = null) {
    const eligible = eligibleVariantIds ? new Set(eligibleVariantIds.map(String)) : null;
    const gifts = giftUnits(cart);
    return (cart.items || []).reduce((total, item) => {
      if (eligible && !eligible.has(variantId(item))) return total;
      const quantity = Number(item.quantity) - (gifts.get(item.key) || 0);
      return total + Math.round(Number(item.original_price) * factor) * quantity;
    }, 0);
  }

  function discountValue(cart, code = '', excludedCodes = []) {
    const codes = new Set((cart.discount_codes || []).map((discount) => discount.code));
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
    ];
    applications.forEach((discount) => {
      if (discount?.type === 'discount_code') codes.add(discount.title);
    });
    codes.add(code.trim());
    const excluded = new Set(excludedCodes.map((value) => value.trim().toLowerCase()));
    const remaining = new Map();
    codes.forEach((value) => {
      if (typeof value !== 'string' || !value.trim()) return;
      const trimmed = value.trim();
      const key = trimmed.toLowerCase();
      if (!excluded.has(key) && !remaining.has(key)) remaining.set(key, trimmed);
    });
    return [...remaining.values()].join(',');
  }

  function buildSubmission(cart, targets, config) {
    const updates = buildUpdates(cart, targets);
    const currentGifts = giftUnits(cart);
    const purchased = quantities(cart);
    targets.forEach(({ id, quantity }) => purchased.set(String(id), quantity));
    const hasProducts = [...purchased.values()].some((quantity) => quantity > 0);
    const reached = Boolean(config.enabled && Number(config.threshold) > 0 && config.subtotal >= Number(config.threshold) && hasProducts);
    const code = String(config.discountCode || '').trim();
    const previousCode = String(storedRule(cart)?.discountCode || '').trim();
    // update.js cannot add line properties. Track the one automatic unit in a
    // cart attribute, while retaining the properties of existing purchased lines.
    currentGifts.forEach((quantity, key) => {
      const item = cart.items.find((item) => item.key === key);
      updates[key] = (updates[key] ?? item.quantity) - quantity;
    });
    const rule = {
      variantId: config.variantId, threshold: config.enabled ? config.threshold : 0,
      currency: config.currency, eligibleVariantIds: config.eligibleVariantIds, discountCode: code,
      gift: { quantity: 0 },
    };
    if (reached) {
      const id = String(config.variantId);
      const matching = (cart.items || []).filter((item) => variantId(item) === id);
      const legacy = matching.find((item) => item.properties?.[GIFT] === '1');
      const existing = legacy || preferredLine(matching);
      const key = existing?.key || id;
      updates[key] = (updates[key] ?? existing?.quantity ?? 0) + 1;
      if (!legacy || updates[key] > 1) rule.gift = {
        quantity: 1, lineKey: existing?.key || '', properties: existing?.properties || {}, sellingPlanId: existing ? sellingPlanId(existing) : '',
      };
    }
    const body = { updates };
    const wasActive = Boolean(cart.attributes?.[RULE]) || (cart.items || []).some((item) => item.properties?.[RULE] || item.properties?.[GIFT] === '1');
    if (config.enabled || wasActive) body.attributes = { [RULE]: hasProducts ? JSON.stringify(rule) : '' };
    const excludedCodes = [previousCode, code].filter((value) => value && (!reached || value.toLowerCase() !== code.toLowerCase()));
    const discount = discountValue(cart, reached ? code : '', excludedCodes);
    if ((reached && code) || discount !== discountValue(cart)) body.discount = discount;
    return body;
  }

  async function request(path, body) {
    if (window.ScQuickOrderGift) return window.ScQuickOrderGift.request(path, body);
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const cart = await response.json();
    if (!response.ok || cart.status || cart.errors) throw new Error(cart.description || cart.message || cart.errors || '');
    return cart;
  }

  window.ScQuickOrderCart = { storedRule, allocatedGiftLine, giftUnits, linesByVariant, quantities, buildUpdates, buildSubmission, eligibleSubtotal, discountValue, request };
})();
